import { Box } from '@mui/material';
import { motion } from 'framer-motion';
import type React from 'react';
import { useEffect, useRef, useState } from 'react';

interface GradeCheckRevealProps {
	/** Formatted grade, e.g. `4,3`. */
	label: string;
	/** Grade colour — the check is drawn in the same colour as the value. */
	color: string;
	/** Play the check-then-value sequence (true only on a genuine grade change). */
	animate: boolean;
	/** Called once the sequence actually played, so the change can be marked seen. */
	onRevealed?: () => void;
	/** Sizing of the check: expanded card vs the small collapsed badge. */
	variant?: 'card' | 'badge';
}

const DRAW_MS = 450;
const HOLD_MS = 300;
// How often to re-check whether the stage has cleared (modal closed / tab visible).
const STAGE_POLL_MS = 150;

// The one-shot draw must not play unseen behind a modal (e.g. the
// create-collection dialog is still open when its success flips the grade) or
// in a hidden tab.
const isStageClear = () =>
	document.visibilityState === 'visible' &&
	!document.querySelector('.MuiBackdrop-root:not(.MuiBackdrop-invisible)');

// Sized to each grade line so the row doesn't jump when the value replaces the check.
const CHECK_SIZE = {
	card: { width: '20px', height: '22px' },
	badge: { width: '14px', height: '16px' },
};

/**
 * Grade slot content: normally just the value; on a grade change a checkmark
 * draws itself (framer stroke animation), holds a beat, then the new value
 * fades in. Remounted per grade via `key`, so `animate` is latched at mount —
 * the sequence can't be cancelled by the change-detection flag flipping back.
 * The sequence only starts once the stage is clear (no modal backdrop, tab
 * visible), so the draw isn't wasted where nobody can see it.
 */
const GradeCheckReveal: React.FC<GradeCheckRevealProps> = ({
	label,
	color,
	animate,
	onRevealed,
	variant = 'card',
}) => {
	const [playing] = useState(animate);
	const [showValue, setShowValue] = useState(!animate);
	const [drawing, setDrawing] = useState(false);
	const onRevealedRef = useRef(onRevealed);
	onRevealedRef.current = onRevealed;

	useEffect(() => {
		if (!playing) {
			return undefined;
		}
		let timer: ReturnType<typeof setTimeout> | undefined;
		let poll: ReturnType<typeof setInterval> | undefined;

		const startIfClear = () => {
			if (!isStageClear()) {
				return false;
			}
			setDrawing(true);
			timer = setTimeout(() => {
				setShowValue(true);
				onRevealedRef.current?.();
			}, DRAW_MS + HOLD_MS);
			return true;
		};

		if (!startIfClear()) {
			poll = setInterval(() => {
				if (startIfClear() && poll) {
					clearInterval(poll);
				}
			}, STAGE_POLL_MS);
		}

		return () => {
			if (poll) clearInterval(poll);
			if (timer) clearTimeout(timer);
		};
	}, [playing]);

	if (showValue) {
		return (
			<Box
				component={motion.span}
				sx={{ display: 'inline-block' }}
				initial={playing ? { opacity: 0, y: 4 } : false}
				animate={{ opacity: 1, y: 0 }}
				transition={{ duration: 0.2, ease: 'easeOut' }}
			>
				{label}
			</Box>
		);
	}

	return (
		<Box
			component="svg"
			data-testid="trial-report-card-grade-check"
			viewBox="0 0 24 24"
			aria-hidden="true"
			sx={{ ...CHECK_SIZE[variant], display: 'inline-block', verticalAlign: 'top' }}
		>
			<motion.path
				d="M4 12.5 L10 18.5 L20 6.5"
				fill="none"
				stroke={color}
				strokeWidth={3}
				strokeLinecap="round"
				strokeLinejoin="round"
				initial={{ pathLength: 0 }}
				animate={{ pathLength: drawing ? 1 : 0 }}
				transition={{ duration: DRAW_MS / 1000, ease: 'easeOut' }}
			/>
		</Box>
	);
};

export default GradeCheckReveal;
